import React, { useContext, useEffect, useState } from 'react';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import MultiSelectEntity from '../RefineSearch/MultiSelectEntity';
import Popup from '../Common/Popup';

/**
 * The `CountryFilter` function is a React component that renders a filter for selecting countries,
 * with options that can be expanded or collapsed.
 * @returns a JSX element.
 */
function CountryFilter({ setCountryOptions, CountryOptions, customPathname, onChange, label = "Country", isPopupOpen, setIsPopupOpen }) {

    const { countries, refineSearchFilters, accessControl } = useContext(PeFilterContext);
    
    // Use internal state only if props are not provided (for backward compatibility)
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;

    useEffect(() => {
        const countryIds = refineSearchFilters[customPathname]?.countries?.map(value => value.id) ?? [];
        setCountryOptions(
            countries?.map(country => ({
                ...country,
                isChecked: countryIds.includes(country.id)
            }))
        );
    }, [countries, customPathname, refineSearchFilters, setCountryOptions]);

    const handleCountryChange = (e) => {
        const { id, checked, name, filteredIds, value } = e.target;
        
        // Handle "Select All" / "Deselect All" functionality
        if (name === 'filteredSelect') {
            const updatedCountries = CountryOptions.map(country =>
                filteredIds.includes(country.id) ? { ...country, isChecked: checked } : country
            );
            setCountryOptions(updatedCountries);
            onChange(updatedCountries)
        } else {
            // Handle individual checkbox changes
            let entityId;
            if(customPathname === '/pe/investors/pe-vc-limited-partners'){
                entityId = id || value;
            } else {
                entityId =  parseInt(id) || parseInt(value);
            }
            const updatedCountries = CountryOptions.map(country =>
                country.id === entityId ? { ...country, isChecked: checked } : country
            );
            setCountryOptions(updatedCountries);
            onChange(updatedCountries)
        }
    };

    const selectedCountryLength = CountryOptions?.filter(option => option.isChecked)?.length ?? 0;

    // Sort countries with India, United States, Singapore first, then rest alphabetically
    const getSortedCountries = (countries) => {
        if (!countries) return [];
        
        const priorityCountries = ['India', 'United States', 'Singapore'];
        
        // Separate priority countries and others
        const priority = [];
        const others = [];
        
        countries.forEach(country => {
            if (priorityCountries.includes(country.name)) {
                priority.push(country);
            } else {
                others.push(country);
            }
        });
        
        // Sort priority countries in the specified order
        priority.sort((a, b) => {
            const indexA = priorityCountries.indexOf(a.name);
            const indexB = priorityCountries.indexOf(b.name);
            return indexA - indexB;
        });
        
        // Sort others alphabetically
        others.sort((a, b) => a.name.localeCompare(b.name));
        
        return [...priority, ...others];
    };

    const sortedCountryOptions = getSortedCountries(CountryOptions);

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100  text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">{label} <span className="opacity-60 ml-2 text-xs text-ellipsis">e.g. India, United States</span></span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {selectedCountryLength > 0 && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {selectedCountryLength}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)} label={label}>
                    <div className="p-4">
                        <MultiSelectEntity
                            handleEntityChange={handleCountryChange}
                            primaryEntity={sortedCountryOptions}
                            disabled={accessControl?.pe_countries?.id?.length > 0
                                ? CountryOptions.filter(op => !accessControl.pe_countries.id.includes(op.id))
                                : []}
                        />
                    </div>
                </Popup>
            )}
        </div>
    );
}

export default CountryFilter;